import { useState, useMemo } from 'react';

const fmt = (n: number) => `${Math.round(n).toLocaleString('pl-PL')} zł`;
const ppFmt = (n: number) => `${n.toFixed(2).replace('.', ',')} p.p.`;
const rateFmt = (n: number) => `${n.toFixed(2).replace('.', ',')}%`;

const annuity = (K: number, r: number, n: number): number => {
  const m = r / 1200;
  if (m === 0) return K / n;
  return (K * m) / (1 - Math.pow(1 + m, -n));
};

type LoanKind = 'obr' | 'inw';

interface SliderFieldProps {
  label: string;
  value: number;
  display: React.ReactNode;
  min: number;
  max: number;
  step: number;
  marks: [string, string, string];
  onChange: (v: number) => void;
}

function SliderField({ label, value, display, min, max, step, marks, onChange }: SliderFieldProps) {
  return (
    <div className="mb-5">
      <div className="flex justify-between items-baseline mb-2">
        <span className="text-[11px] font-bold text-foreground-500 uppercase tracking-wider">{label}</span>
        <span className="text-sm font-extrabold text-foreground-950 tracking-tight">{display}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full cursor-pointer"
      />
      <div className="flex justify-between text-[10px] font-semibold text-foreground-400 mt-1">
        <span>{marks[0]}</span>
        <span>{marks[1]}</span>
        <span>{marks[2]}</span>
      </div>
    </div>
  );
}

export default function BusinessLoanCalculator() {
  const [kind, setKind] = useState<LoanKind>('obr');
  const [amt, setAmt] = useState(300000);
  const [months, setMonths] = useState(36);
  const [margin, setMargin] = useState(3);
  const [income, setIncome] = useState(25000);
  const [debt, setDebt] = useState(0);

  const WIBOR = 3.81;
  const DSCR_MIN = 1.2;
  const maxMonths = kind === 'obr' ? 60 : 180;

  const { rata, rate, allRaty, dscr, interest, maxAmt, pos } = useMemo(() => {
    const rate = WIBOR + margin;
    const rata = annuity(amt, rate, months);
    const allRaty = rata + debt;
    const dscr = allRaty > 0 ? income / allRaty : 99;
    const interest = rata * months - amt;

    const maxRata = Math.max(0, income / DSCR_MIN - debt);
    const mRate = rate / 1200;
    const maxAmt = mRate === 0 ? maxRata * months : maxRata * (1 - Math.pow(1 + mRate, -months)) / mRate;

    const pos = Math.max(0, Math.min(1, (dscr - 0.8) / 1.2)) * 100;

    return { rata, rate, allRaty, dscr, interest, maxAmt, pos };
  }, [amt, months, margin, income, debt]);

  let dscrTag: string;
  let dscrTagClass: string;
  let hintClass: string;
  let hintText: React.ReactNode;

  if (dscr >= 1.5) {
    dscrTag = 'Bardzo dobra zdolność';
    dscrTagClass = 'bg-green-100 text-green-700';
    hintClass = 'bg-primary-100 border border-primary-200 text-primary-800';
    hintText = (
      <>
        Firma z zapasem obsłuży ten kredyt (DSCR {dscr.toFixed(2).replace('.', ',')}). Z taką pozycją warto <b>negocjować marżę</b> — ekspert kredytowy sprawdzi, który bank da najlepsze warunki, i pomoże uzyskać gwarancję BGK obniżającą wymagane zabezpieczenia.
      </>
    );
  } else if (dscr >= DSCR_MIN) {
    dscrTag = 'Zdolność na granicy progu';
    dscrTagClass = 'bg-amber-100 text-amber-700';
    hintClass = 'bg-amber-50 border border-amber-200 text-amber-800';
    hintText = (
      <>
        Wniosek przejdzie w części banków, ale bez zapasu. Wydłużenie okresu lub inna struktura (np. część w leasingu) poprawi wskaźnik — ekspert kredytowy dobierze wariant, który bank zaakceptuje.
      </>
    );
  } else {
    dscrTag = 'Poniżej progu banków';
    dscrTagClass = 'bg-red-100 text-red-700';
    hintClass = 'bg-red-50 border border-red-200 text-red-800';
    hintText = (
      <>
        Przy tych parametrach większość banków odmówi (DSCR poniżej 1,2). To nie koniec możliwości: dłuższy okres, niższa kwota (maks. ok. <b>{fmt(maxAmt)}</b>), faktoring lub gwarancja BGK potrafią odblokować finansowanie. <b>Zadzwoń: 698 084 044.</b>
      </>
    );
  }

  return (
    <div>
      {/* Hero */}
      <section className="bg-foreground-950 text-white py-14 md:py-20">
        <div className="mx-auto max-w-[1440px] px-4 md:px-8 lg:px-16 xl:px-20">
          <div className="text-[11px] font-bold tracking-[0.2em] uppercase text-primary-400 mb-3 opacity-0 animate-slide-up animation-delay-100">
            Dla firm · Kredyt firmowy
          </div>
          <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-white mx-auto max-w-2xl text-center leading-tight mb-4 opacity-0 animate-slide-up animation-delay-200">
            Kredyt dla firmy — policz ratę i sprawdź, czy bank powie "tak"
          </h1>
          <p className="text-sm md:text-base text-white/60 mx-auto max-w-xl text-center leading-relaxed opacity-0 animate-slide-up animation-delay-300">
            Kalkulator liczy ratę kredytu obrotowego lub inwestycyjnego i ocenia zdolność Twojej firmy tak, jak robi to analityk bankowy — wskaźnikiem pokrycia obsługi długu (DSCR).
          </p>
        </div>
      </section>

      {/* Calculator */}
      <section className="py-10 md:py-14 bg-background-50">
        <div className="mx-auto max-w-[1440px] px-4 md:px-8 lg:px-16 xl:px-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 items-start">
            {/* Inputs */}
            <div className="bg-white rounded-2xl border border-background-200 p-5 md:p-6 shadow-sm">
              <h3 className="font-heading text-base md:text-lg font-bold text-foreground-950 mb-5">
                Twoja firma i kredyt
              </h3>

              <div className="mb-5">
                <div className="flex justify-between items-baseline mb-2">
                  <span className="text-[11px] font-bold text-foreground-500 uppercase tracking-wider">Rodzaj kredytu</span>
                </div>
                <div className="grid grid-cols-2 border-[1.5px] border-background-200 rounded-xl overflow-hidden">
                  <button
                    type="button"
                    onClick={() => { setKind('obr'); if (months > 60) setMonths(36); }}
                    className={`py-2.5 text-sm font-bold transition-colors cursor-pointer whitespace-nowrap ${kind === 'obr' ? 'bg-primary-500 text-white' : 'bg-white text-foreground-500 hover:bg-background-100'}`}
                  >
                    Obrotowy
                  </button>
                  <button
                    type="button"
                    onClick={() => setKind('inw')}
                    className={`py-2.5 text-sm font-bold transition-colors cursor-pointer whitespace-nowrap ${kind === 'inw' ? 'bg-primary-500 text-white' : 'bg-white text-foreground-500 hover:bg-background-100'}`}
                  >
                    Inwestycyjny
                  </button>
                </div>
              </div>

              <SliderField
                label="Kwota kredytu"
                value={amt}
                display={<>{amt.toLocaleString('pl-PL')} <span className="text-foreground-400 font-semibold text-xs">zł</span></>}
                min={50000}
                max={3000000}
                step={10000}
                marks={['50 tys.', '1,5 mln', '3 mln']}
                onChange={setAmt}
              />

              <SliderField
                label="Okres spłaty"
                value={months}
                display={<>{months} <span className="text-foreground-400 font-semibold text-xs">mies.</span></>}
                min={12}
                max={maxMonths}
                step={12}
                marks={['12', kind === 'obr' ? '36' : '96', String(maxMonths)]}
                onChange={setMonths}
              />

              <SliderField
                label="Marża banku (+ WIBOR 3M 3,81%)"
                value={margin}
                display={<>{ppFmt(margin)}</>}
                min={1}
                max={8}
                step={0.1}
                marks={['1 p.p.', '4,5 p.p.', '8 p.p.']}
                onChange={setMargin}
              />

              <SliderField
                label="Śr. mies. dochód firmy (przychody − koszty)"
                value={income}
                display={<>{income.toLocaleString('pl-PL')} <span className="text-foreground-400 font-semibold text-xs">zł</span></>}
                min={3000}
                max={200000}
                step={1000}
                marks={['3 tys.', '100 tys.', '200 tys.']}
                onChange={setIncome}
              />

              <SliderField
                label="Obecne raty firmowe (mies.)"
                value={debt}
                display={<>{debt.toLocaleString('pl-PL')} <span className="text-foreground-400 font-semibold text-xs">zł</span></>}
                min={0}
                max={50000}
                step={500}
                marks={['0', '25 tys.', '50 tys.']}
                onChange={setDebt}
              />
            </div>

            {/* Results */}
            <div className="bg-white rounded-2xl border border-background-200 p-5 md:p-6 shadow-sm">
              <h3 className="font-heading text-base md:text-lg font-bold text-foreground-950 mb-5">
                Ocena wniosku
              </h3>

              <div className="bg-foreground-950 rounded-xl text-white p-6 text-center">
                <div className="text-[10px] font-bold tracking-[0.15em] uppercase text-primary-400">
                  Rata kredytu
                </div>
                <div className="text-2xl md:text-3xl font-extrabold tracking-tight mt-1">
                  {fmt(rata)}
                </div>
                <div className="text-sm text-white/60 mt-1">
                  oprocentowanie {rateFmt(rate)} (WIBOR 3M 3,81% + marża {margin.toFixed(1).replace('.', ',')} p.p.)
                </div>
              </div>

              <div className="mt-5">
                <div className="h-2.5 rounded-full relative" style={{ background: 'linear-gradient(90deg,#dc2626 0%,#d97706 40%,#059669 65%,#059669 100%)' }}>
                  <div
                    className="absolute -top-1.5 w-5 h-5 rounded-full bg-white border-[4px] border-foreground-950 shadow-md transition-all duration-500 ease-out"
                    style={{ left: `${pos}%`, transform: 'translateX(-50%)' }}
                  />
                </div>
                <div className="flex justify-between text-[10px] font-bold text-foreground-400 mt-2 tracking-wide">
                  <span>DSCR 0,8</span>
                  <span>1,2 (próg banków)</span>
                  <span>2,0+</span>
                </div>
                <div className="text-center mt-2">
                  <span className={`inline-block px-4 py-1.5 rounded-full text-xs font-extrabold ${dscrTagClass}`}>
                    {dscrTag}
                  </span>
                </div>
              </div>

              <div className="mt-4 space-y-0">
                <div className="flex justify-between py-2.5 border-b border-background-100 text-sm">
                  <span className="text-foreground-500">Wskaźnik DSCR (dochód / raty)</span>
                  <b className="text-foreground-900">{dscr > 20 ? '>20' : dscr.toFixed(2).replace('.', ',')}</b>
                </div>
                <div className="flex justify-between py-2.5 border-b border-background-100 text-sm">
                  <span className="text-foreground-500">Łączne raty po kredycie</span>
                  <b className="text-foreground-900">{fmt(allRaty)}</b>
                </div>
                <div className="flex justify-between py-2.5 border-b border-background-100 text-sm">
                  <span className="text-foreground-500">Suma odsetek</span>
                  <b className="text-foreground-900">{fmt(interest)}</b>
                </div>
                <div className="flex justify-between py-2.5 text-sm">
                  <span className="text-foreground-500">Szacunkowa maks. kwota kredytu</span>
                  <b className="text-foreground-900">{fmt(maxAmt)}</b>
                </div>
              </div>

              <div className={`border rounded-xl p-4 mt-4 text-sm leading-relaxed ${hintClass}`}>
                {hintText}
              </div>

              <p className="text-[11px] text-foreground-400 leading-relaxed mt-4">
                Model uproszczony: oprocentowanie = WIBOR 3M (3,81%, stan na 25.07.2026) + marża, raty równe. Banki wymagają zwykle DSCR min. 1,2 z dochodu z 12–24 mies. (KPiR, ryczałt wg przelicznika, pełna księgowość — EBITDA). Kredyt obrotowy: zwykle do 36 mies.; inwestycyjny: do 15 lat, często z wkładem 10–20%. Realna ocena zależy od branży, historii w BIK/BR i sezonowości.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-10 md:py-14 bg-background-50">
        <div className="mx-auto max-w-[1440px] px-4 md:px-8 lg:px-16 xl:px-20">
          <div className="bg-foreground-950 rounded-2xl p-8 md:p-12 text-center relative overflow-hidden">
            <div className="absolute -right-20 -top-20 w-60 h-60 rounded-full bg-primary-500/10 blur-3xl pointer-events-none" />
            <div className="relative z-10">
              <h2 className="font-heading text-xl md:text-2xl lg:text-3xl font-bold text-white mb-3">
                Finansowanie dla firm to nie tylko kredyt — sprawdźmy wszystkie opcje
              </h2>
              <p className="text-sm text-white/60 max-w-xl mx-auto mb-8 leading-relaxed">
                Kredyt, leasing, faktoring, gwarancje BGK de minimis — ekspert kredytowy dobierze strukturę finansowania do przepływów Twojej firmy i zbierze oferty z wielu banków. Pierwsza rozmowa bezpłatna.
              </p>
              <a
                href="tel:698084044"
                className="inline-flex items-center gap-3 px-8 py-4 bg-primary-500 text-white font-extrabold text-lg rounded-full hover:bg-primary-600 transition-all hover:-translate-y-0.5 shadow-lg shadow-primary-500/20 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line text-xl" />
                698 084 044
              </a>
              <div className="flex flex-wrap gap-5 justify-center mt-6">
                {[
                  'Kredyty, leasing, faktoring',
                  'Gwarancje BGK',
                  'Bez zobowiązań',
                ].map((t) => (
                  <span key={t} className="flex items-center gap-2 text-xs text-white/50">
                    <i className="ri-check-line text-green-400" />
                    {t}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Legal */}
      <section className="pb-10 md:pb-14 bg-background-50">
        <div className="mx-auto max-w-[1440px] px-4 md:px-8 lg:px-16 xl:px-20">
          <p className="text-[11px] text-foreground-400 leading-relaxed text-center max-w-3xl mx-auto">
            Kalkulator ma charakter poglądowy i edukacyjny — nie stanowi oferty w rozumieniu art. 66 Kodeksu cywilnego ani zapewnienia o udzieleniu finansowania. Decyzja kredytowa zależy od indywidualnej oceny przez bank.
          </p>
        </div>
      </section>
    </div>
  );
}